import React, {useState} from 'react';
import {Route, Routes} from 'react-router-dom';
import axios from 'axios';
import {confirmAlert} from 'react-confirm-alert';
import {isMobile, isTablet} from 'react-device-detect';

/* CSS */
import 'assets/css/Common.css';
import GlobalStyles from 'assets/js/GlobalStyles';

/* MODAL */
import {ModalProvider} from 'hooks/modalContext';

/* JS */
import setAuthorizationToken from 'assets/js/Server';

/* PAGE */
import MainPage from 'pages/main';
import Layout from 'layout/Layout';
import Loading from 'layout/Loading';
import Login from 'pages/login';
import Password from 'pages/password';
import ClientManagement from 'pages/client/management';
import CustomerReport from 'pages/client/customerReport';
import ClientDispatch from 'pages/client/dispatch';
import WorkforceAdministrator from 'pages/workforce/administrator';
import WorkforceDispatch from 'pages/workforce/dispatch';
import WorkforceCheckIn from 'pages/workforce/checkIn';
import WorkforceCheckOut from 'pages/workforce/checkOut';
import WorkforceCommute from 'pages/workforce/commute';
import Barcode from 'pages/workforce/barcode';
import QRCode from 'components/client/ClientBarcode';
import StorageProductType from 'pages/storage/product/type';
import StorageWarehousing from 'pages/storage/warehousing';
import StorageProductStatus from 'pages/storage/product/status';
import StorageProductRetrieval from 'pages/storage/product/retrieval';
import Statistics from 'pages/storage/product/statistics';
import ProductCheckOut from 'pages/storage/product/checkout';
import ClientType from 'pages/client/clientType';
import MeetingList from 'pages/client/meeting/list';
import MeetingWrite from 'pages/client/meeting/write';
import MeetingModify from 'pages/client/meeting/modify';
import MeetingDetail from 'pages/client/meeting/detail';
import ManageCapture from 'pages/client/manageCapture';
import Scanner from 'components/client/Scanner';
import ClientCheck from 'pages/client/clientCheck';
import ExcelReader from "./pages/client/excelReader";
import CheckListManage from "./pages/client/checkList";
import CheckListRetrieve from "./pages/client/checkListRetrieve";
import CustomerMessage from "./pages/client/CustomerMessage";
import PestTrends from "./pages/customer/pestTrends";
import TodayStatus from "./pages/customer/todayStatus";
import Complaint from "./pages/customer/complaint"
import ComplaintWrite from "./pages/customer/complaintWrite"
import CustomerLayout from "./layout/CustomerLayout";
import ProtectedRoute from "./components/customer/ProtectedRoute";
import Info from "pages/info";
import Notification from './pages/customer/notification';
import NotificationDetail from './pages/customer/notificationDetail';
import ClientComplaint from './pages/client/clientComplaint';
import ClientComplaintWrite from './pages/client/clientComplaintWrite';
import EmployRegister from './pages/admin/employRegi';
import ShopifySales from './pages/admin/shopifySales';
import RecommendCurrent from './pages/admin/recommend';
import IncentiveCurrent from './pages/admin/incentives';
import Organization from './pages/admin/organization';
import IncentiveOrganization from './pages/admin/incentiveOrg';
import PrivacyPolicy from './pages/privacy';
import Support from './pages/support';
import ComplaintDetail from './pages/customer/complaintDetail';
import MkbSupport from './pages/mkbSupport';
import BestProduct from './pages/admin/sales-item';
import DiscountManage from './pages/admin/discount-manage';
import DiscountCodeIncentiveCurrent from './pages/admin/discount-code-incentives';
import CustomerService from './pages/customer/customerService';
import CustomerCommercial from './pages/customer/customerCommercial';
import CustomerResidential from './pages/customer/customerResidential';
import ConsultType from './pages/consult/consultType';
import SignUp from './pages/sign-up';

function App() {
  const [loading, setLoading] = useState(false);

  let isRefreshing = false;

  const axiosInstance = axios.create({
    baseURL: 'https://api.ustind.in/',
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    },
  });

  axios.interceptors.request.use(async (config) => {
    if (!isRefreshing) {
      setLoading(true);

      return config;
    }
  }, (error) => {
    return Promise.reject(error);
  });

  const refreshAccessToken = async () => {
    try {
      const response = await axiosInstance.post('user/refreshToken', {
        userRefreshToken: localStorage.getItem('refreshToken'),
        userId: localStorage.getItem('userId')
      });

      if (response.data['apiCode'] === 'S001') {
        setAuthorizationToken(response.data['resultObject']['accessToken']);

        localStorage.setItem('accessToken', response.data['resultObject']['accessToken']);
        localStorage.setItem('refreshToken', response.data['resultObject']['refreshToken']);

        return response.data['resultObject']['accessToken'];
      }

      if (response.data['apiCode'] === 'E001' && response.data['apiStatus'] === 'UNAUTHORIZED') {
        // confirmAlert({
        //   title: 'Credentials do not exist.',
        //   buttons: [{
        //     label: 'Yes',
        //     onClick: () => {
        //       localStorage.removeItem('accessToken');
        //       localStorage.removeItem('refreshToken');
        //
        //       document.location.href = '/login';
        //     }
        //   }]
        // });
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        document.location.href = '/login';
      }
    } catch (error) {
      throw error;
    } finally {
      isRefreshing = false;
    }
  };

  axios.interceptors.response.use((response) => {
    setLoading(false);

    return response;
  }, async (error) => {
    setLoading(false);

    const {config, response} = error;

    if (response.status === 403) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();

          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
            config.baseURL = '';

            window.location.reload();
          }
        } catch (error) {
          console.error('토큰 갱신에 실패했습니다.', error);
        }
      }

      return new Promise(() => {
      });
    }

    return Promise.reject(error);
  });

  return (
    <>
      <Loading loading={loading}/>

      <ModalProvider>
        <GlobalStyles/>
        <ProtectedRoute>
          {!isMobile && !isTablet && (
            <Routes>
              <Route path='/login' element={<Login/>}/>
              <Route path='/signup' element={<SignUp/>}/>
              <Route path='/password' element={<Password/>}/>

              <Route element={<Layout/>}>
                <Route path='*' element={<MainPage/>}/>
                <Route path='/home' element={<MainPage/>}/>
                <Route path='/client/management' element={<ClientManagement/>}/>
                <Route path='/client/dispatch' element={<ClientDispatch/>}/>
                <Route path='/client/type' element={<ClientType/>}/>
                <Route path='/client/capture' element={<ManageCapture/>}/>
                <Route path='/client/report' element={<CustomerReport/>}/>
                <Route path='/client/meeting' element={<MeetingList/>}/>
                <Route path='/client/meeting/write' element={<MeetingWrite/>}/>
                <Route path='/client/meeting/modify' element={<MeetingModify/>}/>
                <Route path='/client/meeting/detail' element={<MeetingDetail/>}/>
                <Route path='/client/check' element={<ClientCheck/>}/>
                <Route path='/client/excel' element={<ExcelReader/>}/>
                <Route path='/client/checklist' element={<CheckListManage/>}/>
                <Route path='/client/checklist/retrieve' element={<CheckListRetrieve/>}/>
                <Route path='/client/message' element={<CustomerMessage/>}/>


                <Route path='/workforce/administrator' element={<WorkforceAdministrator/>}/>
                <Route path='/workforce/dispatch' element={<WorkforceDispatch/>}/>
                <Route path='/workforce/checkIn' element={<WorkforceCheckIn/>}/>
                <Route path='/workforce/checkOut' element={<WorkforceCheckOut/>}/>
                <Route path='/workforce/commute' element={<WorkforceCommute/>}/>

                <Route path='/storage/product/type' element={<StorageProductType/>}/>
                <Route path='/storage/warehousing' element={<StorageWarehousing/>}/>
                <Route path='/storage/product/status' element={<StorageProductStatus/>}/>
                <Route path='/storage/product/checkout' element={<ProductCheckOut/>}/>
                <Route path='/storage/product/retrieval' element={<StorageProductRetrieval/>}/>
                <Route path='/storage/product/statistics' element={<Statistics/>}/>

                <Route path='/admin/employ' element={<EmployRegister /> }/>
                <Route path='/admin/sales' element={<ShopifySales /> }/>
                <Route path='/admin/recommand' element={<RecommendCurrent /> }/>
                <Route path='/admin/incentive' element={<IncentiveCurrent /> }/>
                <Route path='/admin/best' element={<BestProduct /> }/>
                <Route path='/admin/promo/manage' element={<DiscountManage /> }/>
                <Route path='/admin/promo/incentive' element={<DiscountCodeIncentiveCurrent /> }/>

              </Route>

              <Route path='/customer/service' element={<CustomerService />} />
              <Route path='/customer/service/commercial' element={<CustomerCommercial />} />
              <Route path='/customer/service/residential' element={<CustomerResidential />} />
              <Route path='/customer/consult/:type' element={<ConsultType />} />

              <Route path='/admin/organization' element={<Organization/> }/>
              <Route path='/admin/incentiveorg' element={<IncentiveOrganization /> }/>

              <Route path='/workforce/barcode' element={<Barcode/>}/>

              <Route path='/client/qr' element={<QRCode/>}/>
              <Route path='/client/scan' element={<Scanner/>}/>

              <Route element={<CustomerLayout/>}>
                <Route path='/customer/status' element={<TodayStatus />}/>
                <Route path='/customer/trends' element={<PestTrends />}/>
                <Route path='/customer/complaint' element={<Complaint />}/>
                <Route path='/customer/complaint/write' element={<ComplaintWrite />}/>
                <Route path='/customer/complaint/detail' element={<ComplaintDetail />}/>
                <Route path='/customer/notice' element={<Notification />}/>
                <Route path='/customer/notice/detail' element={<NotificationDetail />} />
                <Route path='/customer/service' element={<CustomerService />} />
                <Route path='/customer/service/commercial' element={<CustomerCommercial />} />
                <Route path='/customer/service/residential' element={<CustomerResidential />} />
                <Route path='/customer/consult/:type' element={<ConsultType />} />
              </Route>
              <Route path='/info' element={<Info/>}/>
              <Route path='/info/privacy' element={<PrivacyPolicy />} />
              <Route path='/info/support' element={<Support />} />
              <Route path='/info/tob/support' element={<MkbSupport />} />
            </Routes>
          )}

          {(isMobile || isTablet) && (
            <Routes>
              {/* Customer Layout 영역 */}
              <Route element={<CustomerLayout />}>
                <Route path='/customer/status' element={<TodayStatus />}/>
                <Route path='/customer/trends' element={<PestTrends />}/>
                <Route path='/customer/complaint' element={<Complaint />}/>
                <Route path='/customer/complaint/write' element={<ComplaintWrite />}/>
                <Route path='/customer/complaint/detail' element={<ComplaintDetail />}/>
                <Route path='/customer/notice' element={<Notification />}/>
                <Route path='/customer/notice/detail' element={<NotificationDetail />} />
              </Route>

              {/* 단독 페이지 */}
              <Route path='/login' element={<Login />} />
              <Route path='/signup' element={<SignUp/>}/>
              <Route path='/password' element={<Password/>}/>
              <Route path='/info' element={<Info />} />
              <Route path='/info/privacy' element={<PrivacyPolicy />} />
              <Route path='/info/support' element={<Support />} />
              <Route path='/info/tob/support' element={<MkbSupport />} />


              {/* 기타 */}
              {/*<Route element={<CustomerLayout />}>*/}
                <Route path='/client/check' element={<ClientCheck />} />
                <Route path='/client/state' element={<ClientComplaint />} />
                <Route path='/client/state/write' element={<ClientComplaintWrite />} />

              <Route path='/customer/service' element={<CustomerService />} />
              <Route path='/customer/service/commercial' element={<CustomerCommercial />} />
              <Route path='/customer/service/residential' element={<CustomerResidential />} />
              <Route path='/customer/consult/:type' element={<ConsultType />} />

              {/*</Route>*/}
              {/* Not Found */}
              <Route path='*' element={<Login />} />
            </Routes>
          )}

        </ProtectedRoute>
      </ModalProvider>
    </>
  );
}

export default App;
